/*
 * Instant Task Alert pop-up (wireframe 03).
 *
 * A corner overlay mounted once in the app shell so an instant task reaches a
 * rep wherever they are in Hridaya, not only on the queue's own list. One is
 * mounted per queue (config: sales, ERC). It is fed
 * by two sources that must agree:
 *
 *   1. The config's relay item / cleared packets (sales: `instant_task` /
 *      `instant_task_cleared`), delivered over
 *      the same chat WebSocket the fresh-lead notification already uses.
 *   2. GET config.pendingPath, called on mount, on tab focus,
 *      every RECONCILE_INTERVAL_MS while the tab is visible, on every chat
 *      socket (re)authentication, and when this rep's claim learns a task is gone.
 *
 * REST reconcile is not belt-and-braces: relay packets are `deliveryType:
 * "oneshot"` and are never replayed, so a rep whose tab was closed (or asleep)
 * when the task fired would otherwise never learn about it — in either
 * direction. The reconcile is therefore AUTHORITATIVE: a task it does not list
 * is removed, which is the only way a missed `instant_task_cleared` packet ever
 * stops showing a dead alert. The single exception is a packet that arrived
 * after the request was issued, which the server could not have known about —
 * see arrived_at_ms in instant_alert_entries.ts — and a response the server's
 * row cap may have truncated, which proves nothing about what it left out. The
 * server omits claimed rows, so a task claimed elsewhere leaves by absence (or
 * by its `instant_task_cleared` packet when the response is capped).
 *
 * Visibility is derived: something shows when at least one pending task has
 * NOT been dismissed, which is how a new instant task brings back an alert the
 * rep closed while older tasks are still pending (the PRD's requirement, and
 * why dismissal is a set of keys — see instant_popup_storage.ts). WHAT shows is
 * user-controlled: `minimized` changes only on a click (–, ×, or the pill), so
 * an arriving task can at most raise the pill, never the full panel.
 *
 * The capability boolean (config.capabilityKey, sales: `is_sales`) comes from
 * the server with the pending list rather than being derived from a client-side
 * role check, the same pattern the sales list already uses for `can_edit`. The
 * relay fan-out reaches every CMS session; this boolean is what keeps the alert
 * to the queue's own reps.
 */

import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import { Link } from 'react-router-dom';
import { CallbackHandler } from '../../../myt_chat/chat/callbacks/handler';
import ConnectionState from '../../../myt_chat/chat/enums/ConnectionState';
import type RelayItem from '../../../myt_chat/chat/packets/RelayItem';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';
import { get_api as getApi, post_api as postApi } from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';
import {
  elapsed_ms as elapsedMs,
  format_elapsed as formatElapsed,
  timer_color_css as timerColorCss
} from './instant_timer';
import type { InstantGoneDetail } from './claim';
import {
  assigned_line as assignedLine,
  byAge,
  dismissal_key as dismissalKey,
  mergeEntries
} from './instant_alert_entries';
import type { InstantEntry } from './instant_alert_entries';
import { DEFAULT_CORNER, isCorner, readPopupState, writePopupState } from './instant_popup_storage';
import type { Corner } from './instant_popup_storage';
import { cornerHeldByOther, holdCorner, subscribeCorners } from './instant_popup_corners';
import type { AlertEventType, InstantAlertConfig } from './types';

// The server clamps `limit` to this same value, so a response of exactly this
// many rows may have been truncated and cannot be treated as the complete
// pending set. One constant so the request and that test can never disagree.
const PENDING_LIMIT = 50;

// Wave 8 instrumentation. The other four PRD events (created / opened /
// completed / auto-completed) are already timestamp columns on the task row;
// only these two have no other source, so this is the whole telemetry surface.
const EVENT_SHOWN = 'shown';
const EVENT_DISMISSED = 'dismissed';

// goserver /instant_pending `data`.
interface PendingResponse {
  data?: { data?: { items?: Record<string, unknown>[] } & Record<string, unknown> };
}

interface Position {
  left: number;
  top: number;
}

// Fire-and-forget: the pop-up must never wait on, or fail because of,
// telemetry. A rejected POST is swallowed — a lost row is a slightly wrong
// dismiss rate, a thrown one would be an alert the rep does not get.
const sendAlertEvent = (
  config: InstantAlertConfig,
  taskUuid: string,
  eventType: AlertEventType
): void => {
  if (!taskUuid) return;
  postApi(getApiUrl(config.alertEventPath), config.alertEventBody(taskUuid, eventType), true).catch(
    () => {}
  );
};

// Matches the list's and the banner's clock so the same task reads the same
// elapsed time in all three places.
const TICK_MS = 1000;

// Both `focus` and `visibilitychange` fire on one alt-tab back, and this
// component is mounted in the app shell for every CMS user on every page.
// Mirrors FOCUS_REFRESH_STALE_MS on the sales list.
const RECONCILE_STALE_MS = 5000;

// Relay packets are oneshot, so a socket gap silently loses a `cleared` packet;
// a focused tab gets no focus event, so only this bounds how long a dead alert
// can stay up.
const RECONCILE_INTERVAL_MS = 60 * 1000;

// The component renders nothing until the capability is set, so a failed
// mount reconcile is retried rather than left to the interval. Bounded so a
// hard-down endpoint is not hammered.
const MOUNT_RECONCILE_ATTEMPTS = 3;
const MOUNT_RECONCILE_RETRY_MS = 5000;

// Distance from the viewport edge in each corner. Large enough to clear the
// page's own chrome, small enough that the pop-up still reads as "in the
// corner".
const EDGE_GAP_PX = 24;

const POPUP_WIDTH_PX = 320;

// The expanded panel must never outgrow the corner it sits in: past this many
// rows the rest are a count and a link to the full list.
const MAX_VISIBLE_ENTRIES = 5;

const PILL_HEIGHT_PX = 44;

const BESIDE_GAP_PX = 12;

// `beside`: another pop-up holds this corner, so sit next to it, inward —
// clamped so it never leaves a viewport too narrow for both (it overlaps the
// other one instead).
const cornerStyle = (corner: Corner, beside = false, viewportWidth = Infinity): CSSProperties => {
  const [vertical, horizontal] = (isCorner(corner) ? corner : DEFAULT_CORNER).split('-');
  const inset = beside
    ? Math.max(
        EDGE_GAP_PX,
        Math.min(
          EDGE_GAP_PX + POPUP_WIDTH_PX + BESIDE_GAP_PX,
          viewportWidth - POPUP_WIDTH_PX - EDGE_GAP_PX
        )
      )
    : EDGE_GAP_PX;
  return { [vertical]: EDGE_GAP_PX, [horizontal]: inset };
};

const subscribeResize = (listener: () => void): (() => void) => {
  window.addEventListener('resize', listener);
  return () => window.removeEventListener('resize', listener);
};

// Which corner a dropped pop-up belongs to: whichever half of the viewport its
// centre landed in. Snapping (rather than free positioning) is deliberate — the
// PRD asks for four corners, and a free position would eventually cover the page
// content the rep is reading.
const nearestCorner = (
  rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
  viewport: { width: number; height: number }
): Corner => {
  const centreX = rect.left + rect.width / 2;
  const centreY = rect.top + rect.height / 2;
  const vertical = centreY < viewport.height / 2 ? 'top' : 'bottom';
  const horizontal = centreX < viewport.width / 2 ? 'left' : 'right';
  return `${vertical}-${horizontal}`;
};

interface InstantAlertPopupProps {
  config: InstantAlertConfig;
}

function InstantAlertPopup({ config }: InstantAlertPopupProps) {
  const cmsUserUuid = getAuthUserInfoOrDefault().uuid || '';
  const { storagePrefix, defaultCorner, legacyDefaultCorner } = config;
  const metaLine = config.metaLine ?? assignedLine;

  const [hasCapability, setHasCapability] = useState(false);
  // Set only by a reconcile (config.activeKey), never by a relay packet: the
  // fan-out reaches off-shift reps too. A queue without an activeKey is always active.
  const [active, setActive] = useState(!config.activeKey);
  const [entries, setEntries] = useState<InstantEntry[]>([]);
  const [corner, setCorner] = useState<Corner>(defaultCorner);
  // Only a dragged corner is persisted, so a changed home corner reaches reps
  // who never moved the pop-up.
  const [cornerChosen, setCornerChosen] = useState(false);
  // A Set in state, replaced (never mutated) so a change re-renders.
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const [minimized, setMinimized] = useState(false);
  // Which uuid's stored state `corner`/`dismissed` currently hold. State, not a
  // ref, and the uuid rather than a boolean: a ref flipped inside the restore
  // effect is already true when the persist effect runs on the SAME commit, and
  // a boolean toggled false→true in one effect body batches to no change at
  // all, so on a uuid switch the outgoing rep's values would still be written
  // under the incoming rep's key.
  const [hydratedUuid, setHydratedUuid] = useState<string | null>(null);
  // Free position while a drag is in flight; null once it snaps to a corner.
  const [dragPos, setDragPos] = useState<Position | null>(null);
  const [now, setNow] = useState(() => new Date());

  const popupRef = useRef<HTMLDivElement>(null);
  const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);
  // Task uuids already reported as shown, so a re-render, a relay packet for a
  // sibling task or a reconcile does not write a second row for the same
  // (rep, task). Scoped to this mount: a reload is a genuinely new impression.
  const shownRef = useRef(new Set<string>());
  // Wall-clock of the last reconcile REQUEST, for the staleness guard below.
  const lastReconcileRef = useRef(0);
  // Monotonic id per reconcile request. Two can be in flight at once (a forced
  // mount retry and a focus reconcile), and since removal is authoritative an
  // older response landing last would delete everything the newer one added.
  const reconcileSeqRef = useRef(0);

  // Restore the rep's corner and dismissals. Keyed on the uuid so a logout/login
  // in the same tab picks up the other rep's preferences instead of inheriting.
  useEffect(() => {
    const stored = readPopupState(cmsUserUuid, {
      prefix: storagePrefix,
      defaultCorner,
      legacyDefaultCorner
    });
    setCorner(stored.corner);
    setCornerChosen(stored.corner_chosen);
    setDismissed(new Set(stored.dismissed_keys));
    setMinimized(stored.minimized);
    setHydratedUuid(cmsUserUuid);
  }, [cmsUserUuid, storagePrefix, defaultCorner, legacyDefaultCorner]);

  // The only writer. setState updaters must stay pure — persisting from inside
  // one double-writes under StrictMode and is a side effect React is free to
  // discard or replay under concurrent rendering. The gate holds until a render
  // carries this uuid's restored values, so the defaults are never written over
  // a stored blob.
  useEffect(() => {
    if (hydratedUuid !== cmsUserUuid) return;
    writePopupState(
      cmsUserUuid,
      {
        corner: cornerChosen ? corner : undefined,
        dismissed_keys: Array.from(dismissed),
        minimized
      },
      { prefix: storagePrefix, defaultCorner }
    );
  }, [
    hydratedUuid,
    cmsUserUuid,
    corner,
    cornerChosen,
    dismissed,
    minimized,
    storagePrefix,
    defaultCorner
  ]);

  // Resolves true on a completed reconcile, false on a failed or superseded
  // one, so the mount retry below can tell "no tasks" from "no answer".
  const reconcile = useCallback(
    (options?: { force?: boolean }): Promise<boolean> => {
      const force = !!(options && options.force);
      const startedAt = Date.now();
      if (!force && startedAt - lastReconcileRef.current < RECONCILE_STALE_MS) {
        return Promise.resolve(true);
      }
      lastReconcileRef.current = startedAt;
      reconcileSeqRef.current += 1;
      const seq = reconcileSeqRef.current;
      return getApi(getApiUrl(`${config.pendingPath}?limit=${PENDING_LIMIT}`), true)
        .then((res: PendingResponse) => {
          // Superseded: a newer request was issued after this one, so applying
          // this (authoritative) response would delete what the newer one added.
          // Resolved false, not true: this request answered nothing, and the
          // mount retry chain must keep going rather than treat it as the answer.
          if (seq !== reconcileSeqRef.current) return false;
          const data = res?.data?.data || {};
          config.onPendingData?.(data);
          setHasCapability(!!data[config.capabilityKey]);
          setActive(!config.activeKey || data[config.activeKey] !== false);
          // The server excludes claimed rows; the filter guards an older server
          // that still listed them. A claimed task must never be re-raised.
          const rawItems = Array.isArray(data.items) ? data.items.map(config.normalize) : [];
          const items = rawItems.filter((i) => !i.claimed_by_ops_uuid);
          // Counted BEFORE the claim filter, so an older server's claimed rows
          // still count toward the cap.
          const complete = rawItems.length < PENDING_LIMIT;
          // The REST list is authoritative for what is still pending: anything it
          // does not mention is gone, including a task whose `cleared` packet this
          // tab missed (oneshot relays are never replayed). Two exceptions: a
          // packet that landed while this request was in flight, which the server
          // could not have known about (hence arrived_at_ms), and a response that
          // may have been truncated by the server's row cap, where "absent" does
          // not mean "gone" — those still merge and update, they just remove
          // nothing.
          setEntries((prev) => {
            // Built from the RAW rows, not the filtered ones: "the server
            // mentioned this uuid" is what dedupe means here, so a prev entry the
            // response reports as claimed is dropped even under the cap, where
            // the truncation guard suppresses removal.
            const seen = new Set(rawItems.map((i) => i.task_uuid));
            return items
              .concat(
                prev.filter(
                  (e) => !seen.has(e.task_uuid) && (!complete || (e.arrived_at_ms ?? 0) > startedAt)
                )
              )
              .sort(byAge);
          });
          return true;
        })
        .catch(
          () =>
            // A failed reconcile leaves whatever the relay has already delivered.
            // Silent on purpose: this runs on every focus, and a toast per failure
            // would be noise on a flaky connection.
            false
        );
    },
    [config]
  );

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const attempt = (n: number) => {
      reconcile({ force: true }).then((ok) => {
        if (cancelled || ok || n >= MOUNT_RECONCILE_ATTEMPTS) return;
        retryTimer = setTimeout(() => attempt(n + 1), MOUNT_RECONCILE_RETRY_MS);
      });
    };
    attempt(1);
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      reconcile();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    // Skipped while hidden; the visibilitychange above catches up on return.
    const intervalId = setInterval(onFocus, RECONCILE_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [reconcile]);

  // Packets sent while the socket was down are never replayed, so every
  // (re)authentication edge must re-sync. Relays are only delivered after auth.
  useEffect(() => {
    let lastState: ConnectionState | null = null;
    const onConnState = (state: ConnectionState) => {
      if (state === ConnectionState.AUTHENTICATED && lastState !== ConnectionState.AUTHENTICATED) {
        reconcile({ force: true });
      }
      lastState = state;
    };
    CallbackHandler.shared.registerConnectionListener(onConnState);
    return () => {
      CallbackHandler.shared.unregisterConnectionListener(onConnState);
    };
  }, [reconcile]);

  useEffect(() => {
    const onGone = (event: Event) => {
      const taskUuid = (event as CustomEvent<InstantGoneDetail>).detail?.task_uuid;
      if (!taskUuid) return;
      setEntries((prev) => prev.filter((e) => e.task_uuid !== taskUuid));
      reconcile({ force: true });
    };
    window.addEventListener(config.goneEvent, onGone);
    return () => {
      window.removeEventListener(config.goneEvent, onGone);
    };
  }, [reconcile, config.goneEvent]);

  // Its own relay listener rather than an extra branch in the app shell's
  // handler: the shell's switch owns the fresh-lead OS notification and the
  // Twilio call packets, and this component's lifecycle (mount/unmount) is what
  // should govern the subscription.
  useEffect(() => {
    const handler = (item: RelayItem) => {
      const raw = item?.data as Record<string, unknown> | undefined;
      if (!raw) return;
      if (raw.type === config.relayItemType) {
        const payload = config.normalize(raw);
        if (!payload.task_uuid) return;
        // The fan-out targets the queue's reps only, so receiving one of
        // these packets IS the capability. It is not `active`: only a
        // reconcile knows whether this rep is on shift.
        setHasCapability(true);
        setEntries((prev) => mergeEntries(prev, payload));
        return;
      }
      if (raw.type === config.relayClearedType) {
        const payload = config.normalize(raw);
        if (!payload.task_uuid) return;
        setEntries((prev) => prev.filter((e) => e.task_uuid !== payload.task_uuid));
      }
    };
    CallbackHandler.shared.registerRelayItemListener(handler);
    return () => {
      CallbackHandler.shared.unregisterRelayItemListener(handler);
    };
  }, [config]);

  const hasUndismissed = entries.some((e) => !dismissed.has(dismissalKey(e)));
  const expanded = hasCapability && active && hasUndismissed && !minimized;

  // shown = rendered as a row of the expanded panel; the pill's count is not an impression.
  useEffect(() => {
    if (!expanded) return;
    entries.slice(0, MAX_VISIBLE_ENTRIES).forEach((entry) => {
      const uuid = entry.task_uuid;
      const key = dismissalKey(entry);
      if (!uuid || dismissed.has(key) || shownRef.current.has(key)) return;
      shownRef.current.add(key);
      sendAlertEvent(config, uuid, EVENT_SHOWN);
    });
  }, [entries, dismissed, expanded, config]);

  const visible = hasCapability && active && entries.length > 0 && hasUndismissed;

  // One clock for every timer in the pop-up, running only while it renders.
  useEffect(() => {
    if (!visible) return undefined;
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(id);
  }, [visible]);

  const owner = config.testIdPrefix;
  useEffect(() => {
    holdCorner(owner, visible ? corner : null);
  }, [owner, visible, corner]);
  useEffect(() => () => holdCorner(owner, null), [owner]);
  const beside = useSyncExternalStore(subscribeCorners, () =>
    config.yieldCorner ? cornerHeldByOther(owner, corner) : false
  );
  const viewportWidth = useSyncExternalStore(subscribeResize, () => window.innerWidth);

  // Pointer Events, so the pop-up drags with a finger or a pen as well as a
  // mouse. Pointer capture also means the move/up pair keeps arriving on the
  // handle after the cursor leaves it, which is what the old window-level
  // mousemove/mouseup listeners were for.
  const onDragMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const offset = dragOffsetRef.current;
    if (!offset) return;
    setDragPos({ left: event.clientX - offset.x, top: event.clientY - offset.y });
  }, []);

  const onDragEnd = useCallback(() => {
    if (!dragOffsetRef.current) return;
    dragOffsetRef.current = null;
    const node = popupRef.current;
    if (node) {
      setCorner(
        nearestCorner(node.getBoundingClientRect(), {
          width: window.innerWidth,
          height: window.innerHeight
        })
      );
      setCornerChosen(true);
    }
    setDragPos(null);
  }, []);

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    // Right- and middle-click must not start a drag: they deliver no matching
    // pointerup on the handle.
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // CONSTRAINT: a pointer-down on the dismiss button must not start a drag.
    // Capturing the pointer on the handle retargets the pointer-up and the click
    // to the handle, so the button's onClick would never fire.
    if ((event.target as HTMLElement).closest('button')) return;
    const node = popupRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    dragOffsetRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    setDragPos({ left: rect.left, top: rect.top });
    const handle = event.currentTarget;
    if (handle && handle.setPointerCapture && event.pointerId !== undefined) {
      try {
        handle.setPointerCapture(event.pointerId);
      } catch {
        // Without capture a release outside the handle delivers neither
        // pointerup nor pointercancel, which would leave the pop-up stuck at its
        // free position and resume dragging on the next move with no button
        // down. Abort instead: the pop-up stays in its corner.
        dragOffsetRef.current = null;
        setDragPos(null);
      }
    }
  };

  const dismiss = () => {
    // Only tasks that were reported shown and not already dismissed, so the
    // dismiss rate keeps a matching denominator.
    entries
      .filter((e) => !dismissed.has(dismissalKey(e)) && shownRef.current.has(dismissalKey(e)))
      .forEach((e) => sendAlertEvent(config, e.task_uuid, EVENT_DISMISSED));
    setDismissed((prev) => {
      const next = new Set(prev);
      entries.forEach((e) => next.add(dismissalKey(e)));
      return next;
    });
    setMinimized(true);
  };

  const minimize = () => setMinimized(true);
  const expand = () => setMinimized(false);

  // Dismissal hides the pop-up, it does not drop the tasks: a dismissed task is
  // still counted and listed once a NEW task brings the pill back.
  if (!visible) return null;

  const multiple = entries.length > 1;
  const visibleEntries = entries.slice(0, MAX_VISIBLE_ENTRIES);
  const hiddenCount = entries.length - visibleEntries.length;
  const position: CSSProperties = dragPos
    ? { left: dragPos.left, top: dragPos.top }
    : cornerStyle(corner, beside, viewportWidth);

  const timerOf = (entry: InstantEntry) => {
    const ms = elapsedMs(entry.created_at_iso, now);
    return {
      text: ms === null ? '' : formatElapsed(ms),
      color: timerColorCss(ms === null ? 0 : ms)
    };
  };

  // Inline because the CMS-wide `button {}` rule beats utility classes.
  const headerButtonStyle: CSSProperties = {
    margin: 0,
    padding: 0,
    width: 28,
    height: 28,
    borderRadius: 6,
    background: 'transparent',
    border: 'none',
    color: 'var(--state-warning-text)',
    fontSize: 16,
    lineHeight: 1,
    cursor: 'pointer'
  };

  const dot = (
    <span
      aria-hidden="true"
      className="inline-block shrink-0 rounded-full"
      style={{ width: 8, height: 8, background: 'var(--state-warning)' }}
    />
  );

  const timerStyle = (color: string): CSSProperties => ({
    color,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums'
  });

  // CONSTRAINT: everything renders inside the one fixed box — no wrapper or
  // backdrop — so the page around it stays clickable.
  if (!expanded) {
    const oldest = timerOf(entries[0]);
    return (
      <button
        type="button"
        data-testid={`${config.testIdPrefix}-pill`}
        data-corner={corner}
        aria-expanded={false}
        onClick={expand}
        className="fixed flex items-center gap-2 rounded-full border text-[13px]"
        style={{
          ...cornerStyle(corner, beside, viewportWidth),
          margin: 0,
          padding: '0 16px',
          height: PILL_HEIGHT_PX,
          zIndex: 1200,
          background: 'var(--surface-card)',
          borderColor: 'var(--state-warning)',
          boxShadow: 'var(--shadow-modal)',
          color: 'var(--text-primary)',
          cursor: 'pointer'
        }}
      >
        {dot}
        <span className="font-bold">
          {entries.length === 1
            ? `1 ${config.nounSingular}`
            : `${entries.length} ${config.nounPlural}`}
        </span>
        <span style={timerStyle(oldest.color)}>{oldest.text}</span>
      </button>
    );
  }

  return (
    <div
      ref={popupRef}
      data-testid={`${config.testIdPrefix}-popup`}
      data-corner={corner}
      className="fixed flex flex-col overflow-hidden rounded-[10px] border text-[13px]"
      style={{
        ...position,
        width: POPUP_WIDTH_PX,
        maxHeight: '70vh',
        zIndex: 1200,
        background: 'var(--surface-card)',
        borderColor: 'var(--state-warning)',
        boxShadow: 'var(--shadow-modal)',
        color: 'var(--text-primary)'
      }}
    >
      {/* The header is the drag handle — the body holds the links, and dragging
          from a link would fight the click that opens the task. */}
      <div
        onPointerDown={startDrag}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        onPointerCancel={onDragEnd}
        className="flex flex-shrink-0 items-center gap-2 py-2 pl-3 pr-2.5"
        style={{
          background: 'var(--state-warning-bg)',
          color: 'var(--state-warning-text)',
          cursor: 'move',
          userSelect: 'none',
          // Without this a touch drag scrolls the page instead of moving the
          // pop-up, and no pointermove is delivered.
          touchAction: 'none'
        }}
      >
        {dot}
        <span className="text-[13px] font-bold">
          {multiple ? `${config.title}: ${entries.length} pending` : config.title}
        </span>
        <button
          type="button"
          aria-label={`Minimize ${config.title.toLowerCase()}`}
          onClick={minimize}
          className="ml-auto"
          style={headerButtonStyle}
        >
          –
        </button>
        <button
          type="button"
          aria-label={`Dismiss ${config.title.toLowerCase()}`}
          onClick={dismiss}
          style={headerButtonStyle}
        >
          ×
        </button>
      </div>

      {multiple ? (
        <>
          <div className="flex-1" style={{ minHeight: 0, overflowY: 'auto' }}>
            {visibleEntries.map((entry) => {
              const timer = timerOf(entry);
              return (
                <Link
                  key={entry.task_uuid}
                  to={config.formHref(entry.task_uuid)}
                  className="grid border-t border-line-subtle px-3 py-[9px] no-underline hover:bg-surface-sunken hover:no-underline"
                  style={{
                    gridTemplateColumns: 'minmax(0,1fr) auto',
                    columnGap: 10,
                    color: 'var(--text-primary)'
                  }}
                >
                  <span className="truncate font-semibold">{config.entryLabel(entry)}</span>
                  <span className="text-xs" style={timerStyle(timer.color)}>
                    {timer.text}
                  </span>
                  <span className="col-span-2 text-xs text-content-muted">
                    {entry.student_name} · {metaLine(entry)}
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2 border-t border-line-subtle px-3 py-[9px]">
            <Link
              to={config.listPath}
              className="text-[12.5px] font-semibold text-content-link hover:underline"
            >
              {hiddenCount > 0 ? `and ${hiddenCount} more · Open the list` : 'Open the list'}
            </Link>
            <span className="ml-auto text-[11.5px] text-content-muted">Drag the bar to move</span>
          </div>
        </>
      ) : (
        (() => {
          const entry = entries[0];
          const timer = timerOf(entry);
          return (
            <div className="px-3 py-3">
              <div className="font-semibold">{config.entryLabel(entry)}</div>
              <div className="capitalize">{entry.student_name}</div>
              {/* The reason is the single line that tells a rep why this jumped
                  the queue. Only the one-pending layout has room for it; the
                  compact list drops it, per the wireframe. */}
              <div className="mt-1 text-xs text-content-muted">
                {[entry.reason, metaLine(entry)].filter(Boolean).join(' · ')}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span style={timerStyle(timer.color)}>⏱ {timer.text}</span>
                <Link
                  to={config.formHref(entry.task_uuid)}
                  className="ml-auto text-[12.5px] font-semibold text-content-link hover:underline"
                >
                  {config.openLabel}
                </Link>
              </div>
            </div>
          );
        })()
      )}
    </div>
  );
}

export default InstantAlertPopup;
